/**
 * React Query hooks for Access Management API (UAC-08b)
 *
 * individual tool/source access-policy の一覧・作成・削除、および
 * MCP 鍵（api_keys, scopes=["mcp"]）の一覧・発行・失効を扱う。
 * バックエンドは `/admin/workspaces/:id/access-policies` /
 * `/admin/workspaces/:id/mcp-tokens`（UAC-07, packages/core/src/api/web-ui/admin.ts）。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { getApiClient } from '../lib/api';
import type { SourceReadProfile, CreateAccessPolicyRequest, CreateMcpTokenRequest } from '../types/api';

const ACCESS_MGMT_KEYS = {
  all: ['access-management'] as const,
  policies: (workspaceId: string) => [...ACCESS_MGMT_KEYS.all, 'policies', workspaceId] as const,
  mcpTokens: (workspaceId: string) => [...ACCESS_MGMT_KEYS.all, 'mcp-tokens', workspaceId] as const,
  userSlots: (workspaceId: string) => [...ACCESS_MGMT_KEYS.all, 'user-slots', workspaceId] as const,
};

// ─── access-policy hooks ───

export function useAccessPolicies(workspaceId: string) {
  return useQuery({
    queryKey: ACCESS_MGMT_KEYS.policies(workspaceId),
    queryFn: () => getApiClient().listAccessPolicies(workspaceId),
    staleTime: 1000 * 30,
    enabled: workspaceId.length > 0,
  });
}

export function useCreateAccessPolicy(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAccessPolicyRequest) =>
      getApiClient().createAccessPolicy(workspaceId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ACCESS_MGMT_KEYS.policies(workspaceId) });
    },
  });
}

export function useDeleteAccessPolicy(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (policyId: string) => getApiClient().deleteAccessPolicy(workspaceId, policyId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ACCESS_MGMT_KEYS.policies(workspaceId) });
    },
  });
}

// ─── MCP token hooks ───

export function useMcpTokens(workspaceId: string) {
  return useQuery({
    queryKey: ACCESS_MGMT_KEYS.mcpTokens(workspaceId),
    queryFn: () => getApiClient().listMcpTokens(workspaceId),
    staleTime: 1000 * 30,
    enabled: workspaceId.length > 0,
  });
}

export function useCreateMcpToken(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateMcpTokenRequest) => getApiClient().createMcpToken(workspaceId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ACCESS_MGMT_KEYS.mcpTokens(workspaceId) });
    },
  });
}

export function useDeleteMcpToken(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) => getApiClient().deleteMcpToken(workspaceId, tokenId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ACCESS_MGMT_KEYS.mcpTokens(workspaceId) });
    },
  });
}

/** UCX-05: 再発行（同じ userSlot/policy を維持したまま新鍵発行 + 旧鍵失効）。 */
export function useReissueMcpToken(workspaceId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (tokenId: string) => getApiClient().reissueMcpToken(workspaceId, tokenId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ACCESS_MGMT_KEYS.mcpTokens(workspaceId) });
    },
  });
}

// ─── userSlot 選択肢 hooks (UCX-04) ───

export function useUserSlots(workspaceId: string) {
  return useQuery({
    queryKey: ACCESS_MGMT_KEYS.userSlots(workspaceId),
    queryFn: () => getApiClient().listUserSlots(workspaceId),
    staleTime: 1000 * 30,
    enabled: workspaceId.length > 0,
  });
}

export function useSourceReadProfile(workspaceId: string, principalUserId: string) {
  return useQuery({
    queryKey: [...ACCESS_MGMT_KEYS.all, 'read-profile', workspaceId, principalUserId],
    queryFn: () => getApiClient().getSourceReadProfile(workspaceId, principalUserId),
    staleTime: 0,
    enabled: Boolean(workspaceId && principalUserId),
  });
}

export function useSetSourceReadProfile(workspaceId: string, principalUserId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (profile: SourceReadProfile) => getApiClient().setSourceReadProfile(workspaceId, principalUserId, profile),
    onSuccess: () => { void queryClient.invalidateQueries({ queryKey: ACCESS_MGMT_KEYS.all }); },
  });
}
